import React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { arrangeData } from '@/middlewares/api';
import {cleanMoney, normalizeMoney} from '@/utils/forms.js';

import FormField from '@/components/forms/form-field';
import ManyAutocompleteField from '@/components/forms/many-autocomplete';
import ManyWithImagesAutocompleteField from '@/components/forms/many-with-image-autocomplete';
import WordsLimitedField from '@/components/forms/words-limited-field';
import { FORMS } from '@/static/common.js';
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';

const validationSchema = Yup.object().shape({
    track_title: Yup.string().max(255, 'Track Title must be at most 255 characters').required('Track Title is required'),
    track_link: Yup.string()
        .matches(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|\?v=)([^#&?]*).*/, 'Invalid YouTube link')
        .required('YouTube Link is required'),
    label: Yup.array().min(1, 'Record Label is required').required('Record Label is required'),
    artists: Yup.array().min(1, 'Artist is required').required('Artist is required'),
    project_title: Yup.string().max(255, 'Project Title must be at most 255 characters').required('Name of the Project is required'),
    project_budget: Yup.string() // Using string because it contains '$' and ',' during input
        .required('Project Budget is required'),
    project_synopsis: Yup.string().max(65000, 'Project Synopsis is too long').required('Project Synopsis is required'),
    description_of_use: Yup.string().max(65000, 'Description of use is too long').required('Describe the Scene is required'),
});

const FormFieldFormik = formikFieldAdapter(FormField);
const ManyAutocompleteFieldFormik = formikFieldAdapter(ManyAutocompleteField);
const ManyWithImagesAutocompleteFieldFormik = formikFieldAdapter(ManyWithImagesAutocompleteField);
const WordsLimitedFieldFormik = formikFieldAdapter(WordsLimitedField);

const UploadSyncStep2 = ({ nextStep, prevStep, initialValues, bgImg }) => {
    return (
        <section className="upload-form__sec" style={bgImg}>
            <div className="content">
                <div className="upload-form__top">
                    <h2 className="head-shadow">CLEAR SYNC</h2>
                    <h3 className="head">Step 2 of 4: Projeсt Info</h3>
                </div>
                <div className="form-step__body">
                    <Formik
                        initialValues={{
                            track_title: '',
                            track_link: '',
                            label: [],
                            artists: [],
                            project_title: '',
                            project_budget: '',
                            project_synopsis: '',
                            description_of_use: '',
                            ...initialValues
                        }}
                        validationSchema={validationSchema}
                        enableReinitialize={true}
                        onSubmit={(values) => {
                            const formData = arrangeData(values, null, ["label", "artists"]);
                            formData.project_budget = cleanMoney(formData.project_budget);
                            nextStep(formData, values);
                        }}
                    >
                        {({ values: currentValues, isSubmitting, setFieldValue }) => (
                            <Form className="form-layout" noValidate>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="track_title"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Name of the master recording"
                                            label="Track Title"
                                            note="Name of the master recording you would like to license."
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="track_link"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Link on YouTube"
                                            label="YouTube Link"
                                            note="Copy / Paste URL"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="label"
                                            component={ManyAutocompleteFieldFormik}
                                            required={true}
                                            label="Record Label"
                                            url="clear/label/search"
                                            placeholder={FORMS.PLACEHOLDER_TBD}
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="artists"
                                            component={ManyWithImagesAutocompleteFieldFormik}
                                            required={true}
                                            url="artist/search"
                                            placeholder="Set artist first and last name"
                                            label="Artist"
                                            note="Name of Artist(s) who performed the song"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="project_title"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Name of the project"
                                            label="Name of the Project"
                                            note="Title of your Film, TV Show, Video Game, or Social Media Project."
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="project_budget"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Budget in $"
                                            label="Project Budget"
                                            note="State overall budget for your film project or media project"
                                            input={{
                                                value: normalizeMoney(currentValues.project_budget),
                                                onChange: (e) => {
                                                    const val = e.target ? e.target.value : e;
                                                    const cleaned = cleanMoney(val);
                                                    setFieldValue('project_budget', cleaned);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="project_synopsis"
                                            component={WordsLimitedFieldFormik}
                                            required={true}
                                            maxWords={150}
                                            placeholder="Wish to use the song"
                                            label="Project Synopsis"
                                            note="Brief synopsis of the project for which you wish to use the song"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="description_of_use"
                                            component={WordsLimitedFieldFormik}
                                            required={true}
                                            maxWords={150}
                                            placeholder="Start typing…"
                                            label="Describe the Scene"
                                            note="Describe how and where in your project the song will be used. E.g. if your production is a film, give a description of a scene for which you would like to use the song."
                                        />
                                    </div>
                                </div>
                                <div className="submit-row submit-row_txt">
                                    <button
                                        className="btn btn-second"
                                        onClick={() => prevStep(currentValues)}
                                        type="button"
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="btn-red-primary"
                                        disabled={isSubmitting}
                                        type="submit"
                                    >
                                        Next
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </section>
    );
};

export default UploadSyncStep2;
